---
id: pdf-download-service
title: PDF download service
owner: Backend Team
status: Active
lastReviewed: 2026-02-02
visibility: internal
tags:
  - pdf
  - resume
  - download
  - microservice
---

## Product Overview

### Who it serves

This microservice supports resume export workflows for both individual users and enterprise consumers by generating downloadable PDF files from stored resume data.

### Primary workflow

Authenticated users request a resume PDF through the v2 endpoint, while enterprise systems can request the same output without user-level auth. The service assembles resume HTML, applies formatting and optional watermarking, and returns either the PDF bytes or an uploaded file URL.

### Business rules

User download requests require Firebase authentication and ownership checks, while enterprise downloads rely on upstream authorization. When the `file_url` flag is provided, the service uploads the PDF to temporary storage and returns a URL instead of inline PDF data.

### Outcomes

Callers receive a resume PDF rendered with the correct template and paper format, and the document includes embedded metadata for RMS tracking when resume data is available.

### Out of scope

Resume editing, template management, and enterprise permission logic are handled by other services.

### API documentation

[Internal API Documentation](https://wiki.rezi.ai/apis/pdf-download-service-api-docs)

## Technical Overview

### Architecture flow

Routes in `apps/micro-services/pdf-download-service/pkg/api/routes/routes.go` connect resume download handlers to a download service that fetches resume data from Firestore, renders HTML via the Chrome adaptor, and writes PDFs to storage when requested.

### Entry points

- The API server runs from `apps/micro-services/pdf-download-service/cmd/resume/main.go`
- OpenAPI generation is handled by `apps/micro-services/pdf-download-service/cmd/spec/main.go`

### Key modules

Request handling lives in `apps/micro-services/pdf-download-service/pkg/api/controllers`, PDF generation and watermark logic in `apps/micro-services/pdf-download-service/pkg/services/download.go`, and metadata embedding in `apps/micro-services/pdf-download-service/pkg/services/metadata.go`.

### Data sources and sinks

Resume data and watermark settings are read from Firestore repositories, and generated PDFs can be uploaded to temporary storage via `apps/micro-services/pdf-download-service/pkg/repo/storage/firestorage`.

### Integrations and side effects

The service integrates with Firebase auth middleware, a Chrome-based HTML-to-PDF adaptor, and ExifTool for embedding RMS metadata into the exported PDFs.

### Constraints and risks

Rendering depends on external binaries and configuration for Chrome and ExifTool, and failures in those dependencies can lead to incomplete PDFs or missing metadata.

## Changelog

- 2026-02-03: Can have custom token
- 2026-02-02: Initial documentation.
